import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, field } from '@simonbackx/simple-encoding';
import { GenericBalance } from '../GenericBalance.js';
import { GroupType } from '../GroupType.js';
import { Organization } from '../Organization.js';
import { User } from '../User.js';
import { EmailRecipient } from '../email/Email.js';
import { Replacement } from '../endpoints/EmailRequest.js';
import { compileToInMemoryFilter } from '../filters/InMemoryFilter.js';
import { memberWithRegistrationsBlobInMemoryFilterCompilers } from '../filters/inMemoryFilterDefinitions.js';
import { Member } from './Member.js';
import { MemberPlatformMembership } from './MemberPlatformMembership.js';
import { MemberResponsibilityRecord } from './MemberResponsibilityRecord.js';
import { Registration } from './Registration.js';
import { MemberRegistrationInvitation } from './RegistrationInvitation.js';
const SGV_OUTDATED_AFTER_MS = 1000 * 60 * 60 * 24 * 30 * 9; // 9 months
const SGV_SYNC_UPDATE_TOLERANCE_MS = 1000 * 5; // 5 seconds
/** High-level SGV sync state shown in member lists and used to decide whether attention is needed. */
export var SGVSyncStatus;
(function (SGVSyncStatus) {
    SGVSyncStatus["Never"] = "never";
    SGVSyncStatus["Outdated"] = "outdated";
    SGVSyncStatus["Changed"] = "changed";
    SGVSyncStatus["Ok"] = "ok";
})(SGVSyncStatus || (SGVSyncStatus = {}));
export class MemberWithRegistrationsBlob extends Member {
    registrations = [];
    users = [];
    responsibilities = [];
    platformMemberships = [];
    balances = [];
    registrationInvitations = [];
    doesMatchFilter(filter) {
        try {
            const compiledFilter = compileToInMemoryFilter(filter, memberWithRegistrationsBlobInMemoryFilterCompilers);
            return compiledFilter(this);
        }
        catch (e) {
            console.error('Error while compiling filter', e, filter);
        }
        return false;
    }
    hasAccount(email) {
        return !!this.users.find(u => u.hasAccount && u.email === email);
    }
    getEmailRecipients(subtypes = null) {
        const recipients = [];
        const shared = [];
        shared.push(Replacement.create({
            token: 'firstNameMember',
            value: this.firstName,
        }));
        shared.push(Replacement.create({
            token: 'lastNameMember',
            value: this.details.lastName ?? '',
        }));
        /* shared.push(Replacement.create({
            token: 'outstandingBalance',
            value: Formatter.price(this.outstandingBalance),
        })); */
        if (this.details.email && (subtypes === null || subtypes.includes('member'))) {
            const user = this.users.find(u => u.email === this.details.email) ?? this.users.find(u => !!this.details.alternativeEmails.find(e => e === u.email));
            recipients.push(EmailRecipient.create({
                objectId: this.id,
                memberId: this.id,
                userId: user?.id ?? null,
                firstName: this.details.firstName,
                lastName: this.details.lastName,
                email: this.details.email,
                language: user?.language ?? this.details.language,
                replacements: [
                    ...shared,
                ],
            }));
        }
        // Always add one without email and user. This one will be visible in the member portal by default if no userId/email match is found
        if (subtypes === null || subtypes.includes('member')) {
            recipients.push(EmailRecipient.create({
                objectId: this.id,
                memberId: this.id,
                firstName: this.details.firstName,
                lastName: this.details.lastName,
                email: null,
                language: this.details.language,
                replacements: [
                    ...shared,
                ],
            }));
        }
        if (subtypes === null || subtypes.includes('parents')) {
            for (const parent of this.details.parents) {
                if (parent.email) {
                    const user = this.users.find(u => u.email === parent.email) ?? this.users.find(u => !!parent.alternativeEmails.find(e => e === u.email));
                    recipients.push(EmailRecipient.create({
                        objectId: this.id,
                        memberId: this.id,
                        userId: user?.id ?? null,
                        firstName: parent.firstName,
                        lastName: parent.lastName,
                        email: parent.email,
                        language: user?.language ?? this.details.language,
                        replacements: [
                            ...shared,
                        ],
                    }));
                }
            }
        }
        if (subtypes && subtypes.includes('unverified')) {
            for (const unverifiedEmail of this.details.unverifiedEmails) {
                const user = this.users.find(u => u.email === unverifiedEmail);
                recipients.push(EmailRecipient.create({
                    objectId: this.id,
                    memberId: this.id,
                    userId: user?.id ?? null,
                    email: unverifiedEmail,
                    language: user?.language ?? this.details.language,
                    replacements: [
                        ...shared,
                    ],
                }));
            }
        }
        return recipients;
    }
    getGroups(organization) {
        return this.registrations
            .filter(registration => registration.registeredAt !== null
            && registration.deactivatedAt === null
            && registration.group.type === GroupType.Membership
            && registration.group.periodId === organization.period.period.id)
            .map(registration => registration.group);
    }
    /**
     * Determines whether a member still reflects the latest SGV sync.
     * New membership registrations and old sync timestamps become outdated, while local edits after sync become changed.
     */
    getSGVSyncStatus(options) {
        const activeRegistrations = this.registrations.filter(registration => registration.group.periodId === options.organization.period.period.id);
        if (activeRegistrations.length === 0) {
            return SGVSyncStatus.Ok;
        }
        const lastExternalSync = this.details.lastExternalSync;
        if (!lastExternalSync) {
            return SGVSyncStatus.Never;
        }
        for (const registration of activeRegistrations) {
            if (registration.group.type !== GroupType.Membership || registration.deactivatedAt !== null || registration.registeredAt === null) {
                continue;
            }
            if (registration.registeredAt > lastExternalSync) {
                return SGVSyncStatus.Outdated;
            }
        }
        if (lastExternalSync.getTime() < (options.now ?? new Date()).getTime() - SGV_OUTDATED_AFTER_MS) {
            return SGVSyncStatus.Outdated;
        }
        if (Math.abs(lastExternalSync.getTime() - this.updatedAt.getTime()) < SGV_SYNC_UPDATE_TOLERANCE_MS) {
            return SGVSyncStatus.Ok;
        }
        return SGVSyncStatus.Changed;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(Registration) })
], MemberWithRegistrationsBlob.prototype, "registrations", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(User), version: 32 })
], MemberWithRegistrationsBlob.prototype, "users", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberResponsibilityRecord), version: 263 })
], MemberWithRegistrationsBlob.prototype, "responsibilities", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberPlatformMembership), version: 270 })
], MemberWithRegistrationsBlob.prototype, "platformMemberships", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GenericBalance), version: 375 })
], MemberWithRegistrationsBlob.prototype, "balances", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(MemberRegistrationInvitation), version: 398 })
], MemberWithRegistrationsBlob.prototype, "registrationInvitations", void 0);
export class MembersBlob extends AutoEncoder {
    members = [];
    organizations = [];
    /**
     * Timestamp on which the backend constructed this blob
     *
     * This is encoded, so it can be stored locally
     */
    receivedAt = new Date();
    markReceivedFromBackend() {
        this.receivedAt = new Date();
    }
    get isStale() {
        return MembersBlob.lastPatchedMembersDate !== null && this.receivedAt < MembersBlob.lastPatchedMembersDate;
    }
    static lastPatchedMembersDate = null;
    static markAllStale() {
        MembersBlob.lastPatchedMembersDate = new Date();
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(MemberWithRegistrationsBlob) })
], MembersBlob.prototype, "members", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Organization) })
], MembersBlob.prototype, "organizations", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 329 })
], MembersBlob.prototype, "receivedAt", void 0);
//# sourceMappingURL=MemberWithRegistrationsBlob.js.map